import { Box, Skeleton, Stack, Typography } from '@mui/material';
import type { SxProps } from '@mui/system';
import { CardSection, CardSubSection, InfoTooltip } from '@side/pantry';

const cardSx: SxProps = {
  backgroundColor: 'grey.100',
  borderRadius: 1,
  p: 2,
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
} as const;

interface SummaryCardProps {
  label: string;
  value: string | number;
  isLoading: boolean;
  tooltip?: string;
}

function SummaryCard({ label, value, isLoading, tooltip }: SummaryCardProps) {
  return (
    <Box sx={cardSx}>
      {isLoading ? (
        <Skeleton variant="text" width={80} height={40} />
      ) : (
        <Typography variant="h3" component="span">
          {value}
        </Typography>
      )}
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
        {tooltip && <InfoTooltip title={tooltip} />}
      </Stack>
    </Box>
  );
}

interface Props {
  chargedCount: number;
  exemptCount: number;
  missingPaymentCount: number;
  isPartnerPay: boolean | null;
  isLoading: boolean;
}

export function PlatformFeeSummaryCards({
  chargedCount,
  exemptCount,
  missingPaymentCount,
  isPartnerPay,
  isLoading,
}: Props) {
  let paymentMethod = '–';
  if (isPartnerPay === true) paymentMethod = 'Partner Pay';
  else if (isPartnerPay === false) paymentMethod = 'Individual Pay';

  let paymentCardValue: string | number = missingPaymentCount;
  if (isPartnerPay === true) {
    paymentCardValue = missingPaymentCount === 0 ? 'Yes' : 'No';
  }
  const paymentCardLabel =
    isPartnerPay === true
      ? 'Has entered platform fee method'
      : 'Missing payment methods';

  return (
    <CardSection
      cardTitle="Platform Fee Summary"
      id="platform-fee-summary-card"
    >
      <CardSubSection>
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 2 }}>
          <SummaryCard
            label="Payment Method"
            value={paymentMethod}
            isLoading={isLoading}
          />
          <SummaryCard
            label="Active team members being charged for the platform fee"
            value={chargedCount}
            isLoading={isLoading}
          />
          <SummaryCard
            label="Exempt Team Members"
            value={exemptCount}
            isLoading={isLoading}
            tooltip="The Administrator, Referral Agent, and Transaction Coordinator roles are exempt from platform fee payments"
          />
          <SummaryCard
            label={paymentCardLabel}
            value={paymentCardValue}
            isLoading={isLoading}
          />
        </Stack>
      </CardSubSection>
    </CardSection>
  );
}
